import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

export function useDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    // Query documents by userId (multi-tenant)
    const q = query(
      collection(db, 'documents'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching documents:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const addDocument = async (data) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add documents');
    }

    try {
      await addDoc(collection(db, 'documents'), {
        ...data,
        userId: user.uid, // Multi-tenant: owner by Firebase UID
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding document:', err);
      throw err;
    }
  };

  const updateDocument = async (docId, data) => {
    try {
      await updateDoc(doc(db, 'documents', docId), data);
    } catch (err) {
      console.error('Error updating document:', err);
      throw err;
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await deleteDoc(doc(db, 'documents', docId));
    } catch (err) {
      console.error('Error deleting document:', err);
      throw err;
    }
  };

  const archiveDocument = async (docId) => {
    try {
      await updateDoc(doc(db, 'documents', docId), { archived: true });
    } catch (err) {
      console.error('Error archiving document:', err);
      throw err;
    }
  };

  const unarchiveDocument = async (docId) => {
    try {
      await updateDoc(doc(db, 'documents', docId), { archived: false });
    } catch (err) {
      console.error('Error unarchiving document:', err);
      throw err;
    }
  };

  // Separate archived and active documents
  const activeDocuments = documents.filter(d => !d.archived);
  const archivedDocuments = documents.filter(d => d.archived);

  // Group active documents by type
  const yearlyDocs = activeDocuments.filter(d => d.type === 'yearly');
  const quarterlyDocs = activeDocuments.filter(d => d.type === 'quarterly');
  const monthlyDocs = activeDocuments.filter(d => d.type === 'monthly');
  // Keep legacy support for old types
  const otherDocs = activeDocuments.filter(d => d.type === 'other' || d.type === 'weekly');

  return {
    documents: activeDocuments,
    archivedDocuments,
    yearlyDocs,
    quarterlyDocs,
    monthlyDocs,
    otherDocs,
    loading,
    error,
    addDocument,
    updateDocument,
    deleteDocument,
    archiveDocument,
    unarchiveDocument
  };
}
